import { Service } from '@turizi/shared';
import { useTranslation } from "react-i18next";
import { useEffect, useState, memo } from "react";
import { TextInput, Button, Autocomplete, Loader } from '@mantine/core';
import EditAttrsModal from './edit-attrs-modal';
import EditModifiersModal from './edit-modifiers-modal';
import EditServiceChildren from './edit-service-children';
import { useDeleteService, useUpdateService, useServiceLocations } from '@/hooks/api-hooks';
import { navigate, SERVICES_PATH } from '@/lib/navigation';
import { IconTrash } from '@tabler/icons-react';
import { LexicalEditor } from './lexical-editor';
import LinkedServiceSelector from './linked-service-selector';
import MirrorServiceSelector from './mirror-service-selector';
import ShowMetadata from './show-metadata';

interface Props {
    service: Service;
}

const EditServiceDetails = memo(function EditServiceDetails({ service }: Props) {
    const { t } = useTranslation();
    const [draft, setDraft] = useState<Service>(service);
    const [savingSection, setSavingSection] = useState<string>('none');
    const [cityFocused, setCityFocused] = useState(false);
    const { data: locationsResponse, isLoading: isLoadingLocations } = useServiceLocations(undefined, { enabled: cityFocused });
    const locations = locationsResponse?.data || [];

    const canSave = draft.name !== service.name ||
        draft.name_public !== service.name_public ||
        draft.city !== service.city ||
        draft.observations !== service.observations ||
        draft.default_inventory_slots !== service.default_inventory_slots;

    const {
        mutate: updateService,
        isPending: saving
    } = useUpdateService();
    const deleteService = useDeleteService();

    const saveChanges = async (key: string, val: any) => {
        setSavingSection(key);
        updateService({
            id: service.id,
            [key]: val,
        }, {
            onSuccess: () => {
                setSavingSection('none');
            }
        });
    };

    const handleSave = () => {
        setSavingSection('basic_info');
        updateService({
            id: service.id,
            name: draft.name?.trim(),
            name_public: draft.name_public?.trim(),
            city: draft.city,
            observations: draft.observations,
            default_inventory_slots: draft.default_inventory_slots
        }, {
            onSuccess: () => setSavingSection('none')
        });
    }

    useEffect(() => {
        setDraft(service);
    }, [service]);

    return (
        <div className='space-y-6'>

            {/* Section 1: Service Definition */}
            <div className='space-y-4'>
                <div className="space-y-4">

                    {/* Derived Info (Title, City, Observations) */}
                    <div className='solid-box-border flex flex-wrap w-full gap-4'>
                        <TextInput
                            label={t('name_optional')}
                            className='flex-1 font-semibold'
                            value={draft.name || ''}
                            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        />
                        <TextInput
                            label={t('name_public_optional')}
                            className='flex-1 font-semibold'
                            value={draft.name_public || ''}
                            onChange={(e) => setDraft({ ...draft, name_public: e.target.value })}
                        />
                        <Autocomplete
                            label={t('city')}
                            placeholder={t('city')}
                            value={draft.city || ''}
                            onChange={(value) => setDraft({ ...draft, city: value })}
                            onFocus={() => setCityFocused(true)}
                            rightSection={isLoadingLocations ? <Loader size="xs" /> : null}
                            data={locations}
                        />
                        <div className='flex items-end justify-end'>
                            <Button
                                loading={savingSection === 'basic_info'}
                                disabled={!canSave}
                                size='xs'
                                color='black'
                                radius='xl'
                                onClick={handleSave}
                                className='shrink-0'
                            >
                                {t('save')}
                            </Button>
                        </div>
                    </div>

                    <div className='flex gap-6 flex-wrap'>
                        <EditAttrsModal
                            title={t('service_attributes')}
                            attrType='self_attrs'
                            attrs={draft.self_attrs}
                            item={service}
                            onChange={async (attrs) => {
                                setDraft({ ...draft, self_attrs: attrs });
                                await saveChanges('self_attrs', attrs);
                            }}
                            isSaving={savingSection === 'self_attrs'}
                        />
                        <div className='space-y-2'>
                            <EditAttrsModal
                                title={t('booking_attributes')}
                                attrType='booking_attrs'
                                attrs={draft.booking_attrs}
                                item={service}
                                onChange={async (attrs) => {
                                    setDraft({ ...draft, booking_attrs: attrs });
                                    await saveChanges('booking_attrs', attrs);
                                }}
                                isSaving={savingSection === 'booking_attrs'}
                            />
                            <EditModifiersModal
                                service={service}
                                modifiers={draft.amount_in_modifiers || []}
                                onChange={async (mods) => {
                                    setDraft({ ...draft, amount_in_modifiers: mods });
                                    await saveChanges('amount_in_modifiers', mods);
                                }}
                                isSaving={savingSection === 'amount_in_modifiers'}
                            />
                            <EditModifiersModal
                                service={service}
                                type="cost"
                                modifiers={draft.amount_out_modifiers || []}
                                onChange={async (mods) => {
                                    setDraft({ ...draft, amount_out_modifiers: mods });
                                    await saveChanges('amount_out_modifiers', mods);
                                }}
                                isSaving={savingSection === 'amount_out_modifiers'}
                            />
                        </div>
                    </div>

                </div>
            </div>

            <div className='solid-box-border space-y-2'>
                <div className="font-bold text-gray-900 text-md">
                    {t('description')}
                </div>
                <LexicalEditor
                    initialHtml={draft.observations || ''}
                    onChange={(str) => setDraft({ ...draft, observations: str })}
                    placeholder={t('observations')}
                />
                <div className="flex justify-end">
                    <Button
                        loading={savingSection === 'basic_info'}
                        disabled={
                            draft.name === service.name &&
                            draft.name_public === service.name_public &&
                            draft.city === service.city &&
                            draft.observations === service.observations &&
                            draft.default_inventory_slots === service.default_inventory_slots
                        }
                        size='xs'
                        color='black'
                        radius='xl'
                        onClick={handleSave}
                    >
                        {t('save')}
                    </Button>
                </div>
            </div>

            {/* Is Package? */}
            <div className="solid-box-border space-y-4">
                <div className='flex items-center justify-between'>
                    <div className="font-bold text-gray-900 text-md">
                        {t('child_services')}
                    </div>
                </div>
                <EditServiceChildren service={service} />
            </div>

            <LinkedServiceSelector service={service} />

            <MirrorServiceSelector service={service} />

            {/* Delete Service */}
            <div className="flex justify-end pt-2">
                <div
                    className="flex items-center gap-2 cursor-pointer text-red-500 hover:text-red-700 transition-colors px-4 py-2 rounded-md hover:bg-red-50"
                    onClick={() => {
                        deleteService.mutate(service.id, {
                            onSuccess: () => {
                                navigate(SERVICES_PATH);
                            }
                        });
                    }}>
                    {deleteService.isPending ?
                        <Loader size="xs" color="red" className="animate-spin" />
                        :
                        <IconTrash size={18} stroke={1.5} />
                    }
                    <div>
                        <div className="font-medium">{t('delete')} {t('service')}</div>
                        <div className="text-xs opacity-75 mt-0.5">{t('this_action_cannot_be_undone')}</div>
                    </div>
                </div>
            </div>
            <ShowMetadata item={service} />
        </div >
    );
});

export default EditServiceDetails;
